import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { LinkContainer } from 'react-router-bootstrap';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Collapse from '@mui/material/Collapse';

import ArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import ArrowRight from '@mui/icons-material/KeyboardArrowRight';
import ArrowDown from '@mui/icons-material/KeyboardArrowDown';
import ArrowUp from '@mui/icons-material/KeyboardArrowUp';
import DocumentsIcon from '@mui/icons-material/FolderOpenOutlined';
import DashboardIcon from '@mui/icons-material/SpaceDashboardOutlined';
import CasesIcon from '@mui/icons-material/MedicalServicesOutlined';
import ActivityIcon from '@mui/icons-material/NotificationsOutlined';
import TrainingIcon from '@mui/icons-material/SchoolOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import AdminIcon from '@mui/icons-material/VerifiedUserOutlined';
import SurgeonsIcon from '@mui/icons-material/LocalHospitalOutlined';
import UsersIcon from '@mui/icons-material/GroupOutlined';
import AddIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import CalendarIcon from '@mui/icons-material/DateRangeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import EditIcon from '@mui/icons-material/Edit';
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeIcon from '@mui/icons-material/LightModeOutlined';
import { useTheme } from '@mui/material/styles';

import Logo from '../../assets/case_note_logo.js'
import signOutIcon from '../../assets/ps_logout_icon.svg';
import { useThemeMode } from '../../styles/AppThemeProvider';

import RestrictComponent from '../shared/RestrictComponent';

// import AdminModal from '../modals/admin';
import CaseModal from '../modals/case';
import TrainingModeModal from '../modals/TrainingModeModal';
import DocumentsModal from '../modals/documents-modal';
import CaseExportModal from '../modals/case/CaseExportModal';
import UserProfileModal from '../modals/UserProfile';

import { withFirebase } from '../../firebase';

import { discardActiveCase, setActiveCase } from '../../actions/casesActions';
import {
  closeSidebar,
  turnTrainingModeOff,
  turnTrainingModeOn,
} from '../../actions/sidebarActions';

import { utils } from '../../util';
import { filterRequired } from '../../util/cases';

import { userRoles, routes } from '../../constants';
import { procedureFlows, statuses, cmfExtendedProcedureFlows } from '../../constants/cases';

import './sidebar.scss';

const getCompanyLogoUrl = (logo) => (
  typeof logo === 'string' ? logo : logo?.downloadUrl || ''
);

const {
  GLOBAL_ADMIN,
  COUNTRY_ADMIN,
  DISTRIBUTOR_ADMIN,
  SURGEON,
  ENGINEER,
  SALES_REP,
  LOGISTICS,
  PRACTICE_MANAGER,
  MANUFACTURER,
  RADIOLOGY,
} = userRoles;

const links = [
  {
    id: 0,
    label: 'Dashboard',
    path: '/dashboard',
    icon: (active) => <DashboardIcon color={active ? 'primary' : 'secondary'} />,
  },
  {
    id: 1,
    label: 'Cases',
    path: '/cases',
    icon: (active) => <CasesIcon color={active ? 'primary' : 'secondary'} />,
    // badge: 'requiredCases',
  },
  {
    id: 2,
    label: 'Activity',
    path: '/activity',
    icon: (active) => <ActivityIcon color={active ? 'primary' : 'secondary'} />,
    badge: 'newActivities',
  },
  {
    id: 3,
    label: 'Calendar',
    path: '/calendar',
    icon: (active) => <CalendarIcon color={active ? 'primary' : 'secondary'} />,
  },
];

const settingsLinks = [
  {
    label: 'Distributors',
    path: routes.DISTRIBUTORS,
    roles: [GLOBAL_ADMIN, COUNTRY_ADMIN]
  },
  {
    label: 'Hospitals',
    path: routes.HOSPITALS,
    roles: [GLOBAL_ADMIN, COUNTRY_ADMIN],
  },
  {
    label: 'Radiology',
    path: routes.RADIOLOGY,
    roles: [GLOBAL_ADMIN, COUNTRY_ADMIN],
  },
];

const adminLinks = [
  {
    label: 'Companies',
    path: routes.COMPANIES,
    roles: [GLOBAL_ADMIN],
  },
  {
    label: 'Procedures',
    path: routes.PROCEDURES,
    roles: [GLOBAL_ADMIN],
  },
  {
    label: 'Products',
    path: routes.PRODUCTS,
    roles: [GLOBAL_ADMIN],
  },
  {
    label: 'Engineering',
    path: routes.TEAMS,
    roles: [GLOBAL_ADMIN],
  },
  {
    label: 'Manufacturers',
    path: routes.MANUFACTURERS,
    roles: [GLOBAL_ADMIN],
  },
  {
    label: 'Logistics',
    path: routes.LOGISTICS,
    roles: [GLOBAL_ADMIN],
  },
];

const Sidebar = (props) => {
  const {
    currentUser,
    companySidebarLogoUrl,
    companyLogoLoading,
    firebase,
    discardActiveCase,
    closeSidebar,
    trainingMode,
    turnOff,
    turnOn,
    caseId,
    collapsed,
    setCollapsed,
    isMobile,
    cases = [],
    setActiveCase,
  } = props;

  const path = window.location.pathname;
  const theme = useTheme();
  const { mode, toggleColorMode } = useThemeMode();
  const shouldShowSidebarLogoPlaceholder = !currentUser?.uid || companyLogoLoading;

  // const [adminModalIsOpen, setAdminModalState] = useState(false);
  const [caseModalIsOpen, setCaseModalState] = useState(false);
  const [trainingModalIsOpen, setTrainingModalState] = useState(false);
  const [trainingConfirmModalOpen, setTrainingConfirmModalState] = useState(false);
  const [documentsModalIsOpen, setDocumentsModalState] = useState(false);
  const [isCaseExportModalOpen, toggleCaseExportModal] = useState(false);
  const [isUserProfileModalOpen, toggleUserProfileModal] = useState(false);

  const [adminOpen, setAdminOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const signOut = () => {
    firebase.doSignOut().then(() => {
      window.location = '/';
    });
  };

  const handleModalClose = (isFirstStep, selectedFlow) => {
    setCaseModalState(false);

    if (isFirstStep && typeof isFirstStep === "boolean") {
      if ([procedureFlows.CMF, ...cmfExtendedProcedureFlows].includes(selectedFlow)) {
        setCaseModalState(true);
      } else {
        toggleCaseExportModal(true);
      }
    } else {
      discardActiveCase();
    }
  };

  const toggleTrainingMode = () => {
    if (trainingMode) {
      turnOff();
    } else {
      setTrainingModalState(true);
    }
  };

  const handleSubmit = () => {
    setTrainingModalState(false);
    turnOn();
  };

  const handleNewCaseClick = () => {
    if (trainingMode) {
      setTrainingConfirmModalState(true);
    } else {
      setCaseModalState(true);
    }
  };

  const displayUserRole = (role) => userRoles[role] && userRoles[role].label;

  const dashboard = links[0];

  const onSkipClick = (id) => {
    toggleCaseExportModal(false);
    const item = cases?.find((c) => c.id === id);
    if (item) {
      setActiveCase(item);
      setCaseModalState(true);
    }
  };

  return (
    <div className="sidebar">
      {collapsed ? (
        <>
          <div className="sidebar-main sidebar-main__collapsed">
            {!isMobile && (
              <div className='m-lg d-flex flex-center' style={{ height: 48 }}>
                <div className="sidebar-toggle" style={{ margin: 0 }} onClick={() => setCollapsed(!collapsed)}>
                  <ArrowRight />
                </div>
              </div>
            )}
            {currentUser.role && currentUser.emailVerified ? (
              <List>
                {currentUser.role === RADIOLOGY.name ? (
                  <LinkContainer to={dashboard.path} key={dashboard.id} onClick={closeSidebar}>
                    <ListItem className={dashboard.path === path && 'active-item'}>
                      <ListItemIcon style={{ minWidth: 32, display: 'flex', justifyContent: 'center' }}>
                        {dashboard.icon(dashboard.path === path)}
                      </ListItemIcon>
                    </ListItem>
                  </LinkContainer>
                ) : (
                  links.map((item) => (
                    <LinkContainer to={item.path} key={item.id} onClick={closeSidebar}>
                      <ListItem className={item.path === path && 'active-item'}>
                        <ListItemIcon style={{ minWidth: 32, display: 'flex', justifyContent: 'center' }}>
                          {item.icon(item.path === path)}
                        </ListItemIcon>
                        {item.badge && props[item.badge] > 0 && !collapsed && (
                          <div className="sidebar-badge">{props[item.badge]}</div>
                        )}
                      </ListItem>
                    </LinkContainer>
                  ))
                )}
                {currentUser.role !== RADIOLOGY.name && (
                  <ListItem
                    onClick={toggleTrainingMode}
                    className={trainingMode ? 'active-training-item' : null}
                  >
                    <ListItemIcon style={{ minWidth: 32, display: 'flex', justifyContent: 'center' }}>
                      <TrainingIcon color={trainingMode ? 'primary' : 'secondary'} />
                    </ListItemIcon>
                  </ListItem>
                )}
                {/*<ListItem onClick={() => setDocumentsModalState(true)}>*/}
                {/*  <ListItemIcon style={{ minWidth: 32, display: 'flex', justifyContent: 'center' }}>*/}
                {/*    <img*/}
                {/*      src={documentsIcon}*/}
                {/*      alt="documents-case-icon"*/}
                {/*      className="sidebar-item-icon"*/}
                {/*    />*/}
                {/*  </ListItemIcon>*/}
                {/*</ListItem>*/}
                <RestrictComponent roles={[GLOBAL_ADMIN, COUNTRY_ADMIN, DISTRIBUTOR_ADMIN]}>
                  <ListItem
                    onClick={() => {
                      setCollapsed(false);
                      setSettingsOpen(true);
                    }}
                  >
                    <ListItemIcon style={{ minWidth: 32, display: 'flex', justifyContent: 'center' }}>
                      <SettingsIcon color='secondary' />
                    </ListItemIcon>
                  </ListItem>
                </RestrictComponent>
                <RestrictComponent roles={[GLOBAL_ADMIN]}>
                  <LinkContainer to={routes.ADMIN}>
                    <ListItem
                      className={routes.ADMIN === path && 'active-item'}
                      onClick={() => {
                        setCollapsed(false);
                        setAdminOpen(true);
                      }}
                    >
                      <ListItemIcon style={{ minWidth: 32, display: 'flex', justifyContent: 'center' }}>
                        <AdminIcon color={routes.ADMIN === path ? 'primary' : 'secondary'} />
                      </ListItemIcon>
                    </ListItem>
                  </LinkContainer>
                </RestrictComponent>
                <RestrictComponent roles={[GLOBAL_ADMIN]}>
                  <LinkContainer to={routes.SURGEONS}>
                    <ListItem className={routes.SURGEONS === path && 'active-item'}>
                      <ListItemIcon style={{ minWidth: 32, display: 'flex', justifyContent: 'center' }}>
                        <SurgeonsIcon color={routes.SURGEONS === path ? 'primary' : 'secondary'} />
                      </ListItemIcon>
                    </ListItem>
                  </LinkContainer>
                </RestrictComponent>
                <RestrictComponent roles={[GLOBAL_ADMIN, COUNTRY_ADMIN]}>
                  <LinkContainer to={routes.USERS}>
                    <ListItem className={routes.USERS === path && 'active-item'}>
                      <ListItemIcon style={{ minWidth: 32, display: 'flex', justifyContent: 'center' }}>
                        <UsersIcon color={routes.USERS === path ? 'primary' : 'secondary'} />
                      </ListItemIcon>
                    </ListItem>
                  </LinkContainer>
                </RestrictComponent>
                <RestrictComponent roles={[GLOBAL_ADMIN]}>
                  <LinkContainer to={routes.DOCUMENTS}>
                    <ListItem>
                      <ListItemIcon style={{ minWidth: 32, display: 'flex', justifyContent: 'center' }}>
                        <DocumentsIcon color='secondary' />
                      </ListItemIcon>
                    </ListItem>
                  </LinkContainer>
                </RestrictComponent>
                {/*<div className='border-bottom' style={{ marginTop: 16, marginBottom: 16 }}></div>*/}
                {trainingMode ? (
                  <RestrictComponent
                    roles={[
                      GLOBAL_ADMIN,
                      COUNTRY_ADMIN,
                      DISTRIBUTOR_ADMIN,
                      SURGEON,
                      ENGINEER,
                      SALES_REP,
                      LOGISTICS,
                      PRACTICE_MANAGER,
                      MANUFACTURER,
                    ]}
                  >
                    <ListItem onClick={handleNewCaseClick}>
                      <ListItemIcon style={{ minWidth: 32, display: 'flex', justifyContent: 'center' }}>
                        <AddIcon color='primary' />
                      </ListItemIcon>
                    </ListItem>
                  </RestrictComponent>
                ) : (
                  <RestrictComponent excludeRoles={[MANUFACTURER, LOGISTICS, RADIOLOGY]}>
                    <ListItem onClick={handleNewCaseClick}>
                      <ListItemIcon style={{ minWidth: 32, display: 'flex', justifyContent: 'center' }}>
                        <AddIcon color='primary' />
                      </ListItemIcon>
                    </ListItem>
                  </RestrictComponent>
                )}
              </List>
            ) : (
              <List>
                <ListItem className={dashboard.path === path && 'active-item'}>
                  <ListItemIcon>
                    <DashboardIcon />
                  </ListItemIcon>
                  <ListItemText primary={dashboard.label} />
                </ListItem>
              </List>
            )}
          </div>
          <div className="user-info">
            {/*<div className="user-name-container">*/}
            {/*  <div className="secondary m-t-md">{VERSION_NUMBER}</div>*/}
            {/*</div>*/}
            <div className="sign-out-container" onClick={signOut} style={{ marginLeft: '4px' }}>
              <img src={signOutIcon} alt="sign-out-icon" />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={`sidebar-main${isMobile ? ' sidebar-mobile' : ''}`}>
            <div className='d-flex space-between'>
              <LinkContainer to="/">
                {shouldShowSidebarLogoPlaceholder ? (
                  <Box className="sidebar-logo sidebar-logo__placeholder" />
                ) : companySidebarLogoUrl ? (
                  <Box
                    component="img"
                    src={companySidebarLogoUrl}
                    alt={`${currentUser?.company || 'company'}-sidebar-logo`}
                    className="sidebar-logo sidebar-logo__image"
                  />
                ) : (
                  <Logo color={theme.palette.primary.main} mode={mode} className="sidebar-logo" />
                )}
              </LinkContainer>
              {!isMobile && (
                <div className="sidebar-toggle" onClick={() => setCollapsed(!collapsed)}>
                  <ArrowLeft />
                </div>
              )}
            </div>
            {currentUser.role && currentUser.emailVerified ? (
              <List>
                {currentUser.role === RADIOLOGY.name ? (
                  <LinkContainer to={dashboard.path} key={dashboard.id} onClick={closeSidebar}>
                    <ListItem className={dashboard.path === path && 'active-item'}>
                      <ListItemIcon>
                        <DashboardIcon color={dashboard.path === path ? 'primary' : 'secondary'} />
                      </ListItemIcon>
                      <ListItemText primary={dashboard.label} />
                      {dashboard.badge && props[dashboard.badge] > 0 && (
                        <div className="sidebar-badge">{props[dashboard.badge]}</div>
                      )}
                    </ListItem>
                  </LinkContainer>
                ) : (
                  links.map((item) => (
                    <LinkContainer to={item.path} key={item.id} onClick={closeSidebar}>
                      <ListItem className={item.path === path && 'active-item'}>
                        <ListItemIcon>
                          {item.icon(item.path === path)}
                        </ListItemIcon>
                        <ListItemText primary={item.label} />
                        {item.badge && props[item.badge] > 0 && (
                          <div className="sidebar-badge">{props[item.badge]}</div>
                        )}
                      </ListItem>
                    </LinkContainer>
                  ))
                )}
                {currentUser.role !== RADIOLOGY.name && (
                  <ListItem
                    onClick={toggleTrainingMode}
                    className={trainingMode ? 'active-training-item' : null}
                  >
                    <ListItemIcon>
                      <TrainingIcon color={trainingMode ? 'primary' : 'secondary'} />
                    </ListItemIcon>
                    <ListItemText primary="Training" />
                    {trainingMode && <div className="sidebar-training-badge">Active</div>}
                  </ListItem>
                )}
                {/*<ListItem onClick={() => setDocumentsModalState(true)}>*/}
                {/*  <ListItemIcon>*/}
                {/*    <img*/}
                {/*      src={documentsIcon}*/}
                {/*      alt="documents-case-icon"*/}
                {/*      className={`sidebar-item-icon${isMobile ? ' icon-mobile' : ''}`}*/}
                {/*    />*/}
                {/*  </ListItemIcon>*/}
                {/*  <ListItemText primary="Documents" />*/}
                {/*</ListItem>*/}
                {/*<RestrictComponent roles={[GLOBAL_ADMIN, COUNTRY_ADMIN, DISTRIBUTOR_ADMIN]}>*/}
                {/*  <ListItem onClick={() => setAdminModalState(true)}>*/}
                {/*    <ListItemIcon>*/}
                {/*      <SettingsIcon color='secondary' />*/}
                {/*    </ListItemIcon>*/}
                {/*    <ListItemText primary="Settings" />*/}
                {/*  </ListItem>*/}
                {/*</RestrictComponent>*/}

                <RestrictComponent roles={[GLOBAL_ADMIN, COUNTRY_ADMIN]}>
                  <ListItem
                    button
                    onClick={() => setSettingsOpen((v) => !v)}
                    className={settingsOpen ? 'active-item' : ''}
                  >
                    <ListItemIcon>
                      <SettingsIcon color={settingsOpen ? 'primary' : 'secondary'} />
                    </ListItemIcon>

                    <ListItemText primary="Settings" />

                    {settingsOpen ? (
                      <ArrowUp fontSize="small" />
                    ) : (
                      <ArrowDown fontSize="small" sx={{ color: '#A4A7AE' }} />
                    )}
                  </ListItem>

                  <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
                    <List disablePadding className="sidebar-submenu">
                      {settingsLinks.filter((link) => link.roles?.map((role) => role.name)?.includes(currentUser.role)).map((item) => (
                        <LinkContainer key={item.path} to={item.path} onClick={closeSidebar}>
                          <ListItem
                            button
                            className={`sidebar-submenu-item ${
                              path === item.path ? 'active-subitem' : ''
                            }`}
                          >
                            <ListItemText primary={item.label} />
                          </ListItem>
                        </LinkContainer>
                      ))}
                    </List>
                  </Collapse>
                </RestrictComponent>

                <RestrictComponent roles={[GLOBAL_ADMIN]}>
                  <ListItem
                    button
                    onClick={() => setAdminOpen((v) => !v)}
                    className={adminOpen ? 'active-item' : ''}
                  >
                    <ListItemIcon>
                      <AdminIcon color={adminOpen ? 'primary' : 'secondary'} />
                    </ListItemIcon>

                    <ListItemText primary="Admin" />

                    {adminOpen ? (
                      <ArrowUp fontSize="small" />
                    ) : (
                      <ArrowDown fontSize="small" sx={{ color: '#A4A7AE' }} />
                    )}
                  </ListItem>

                  <Collapse in={adminOpen} timeout="auto" unmountOnExit>
                    <List disablePadding className="sidebar-submenu">
                      {adminLinks.filter((link) => link.roles?.map((role) => role.name)?.includes(currentUser.role)).map((item) => (
                        <LinkContainer key={item.path} to={item.path} onClick={closeSidebar}>
                          <ListItem
                            button
                            className={`sidebar-submenu-item ${
                              path === item.path ? 'active-subitem' : ''
                            }`}
                          >
                            <ListItemText primary={item.label} />
                          </ListItem>
                        </LinkContainer>
                      ))}
                    </List>
                  </Collapse>
                </RestrictComponent>

                <RestrictComponent roles={[GLOBAL_ADMIN, COUNTRY_ADMIN, DISTRIBUTOR_ADMIN]}>
                  <LinkContainer to={routes.SURGEONS} onClick={closeSidebar}>
                    <ListItem className={routes.SURGEONS === path && 'active-item'}>
                      <ListItemIcon>
                        <SurgeonsIcon color={routes.SURGEONS === path ? 'primary' : 'secondary'} />
                      </ListItemIcon>
                      <ListItemText primary="Surgeons" />
                    </ListItem>
                  </LinkContainer>
                </RestrictComponent>
                <RestrictComponent roles={[GLOBAL_ADMIN, COUNTRY_ADMIN]}>
                  <LinkContainer to={routes.USERS} onClick={closeSidebar}>
                    <ListItem className={routes.USERS === path && 'active-item'}>
                      <ListItemIcon>
                        <UsersIcon color={routes.USERS === path ? 'primary' : 'secondary'} />
                      </ListItemIcon>
                      <ListItemText primary="Users" />
                    </ListItem>
                  </LinkContainer>
                </RestrictComponent>
                <RestrictComponent roles={[GLOBAL_ADMIN]}>
                  <LinkContainer to={routes.DOCUMENTS} onClick={closeSidebar}>
                    <ListItem className={routes.DOCUMENTS === path && 'active-item'}>
                      <ListItemIcon>
                        <DocumentsIcon color={routes.DOCUMENTS === path ? 'primary' : 'secondary'} />
                      </ListItemIcon>
                      <ListItemText primary="Documents" />
                    </ListItem>
                  </LinkContainer>
                </RestrictComponent>
                {/*<div className='border-bottom' style={{ marginTop: 16, marginBottom: 16 }} />*/}
                {trainingMode ? (
                  <RestrictComponent
                    roles={[
                      GLOBAL_ADMIN,
                      COUNTRY_ADMIN,
                      DISTRIBUTOR_ADMIN,
                      SURGEON,
                      ENGINEER,
                      SALES_REP,
                      LOGISTICS,
                      PRACTICE_MANAGER,
                      MANUFACTURER,
                    ]}
                  >
                    <ListItem onClick={handleNewCaseClick} className='new-case-item'>
                      <ListItemIcon>
                        <AddIcon color='primary' />
                      </ListItemIcon>
                      <ListItemText primary="New Case" />
                    </ListItem>
                  </RestrictComponent>
                ) : (
                  <RestrictComponent excludeRoles={[MANUFACTURER, LOGISTICS, RADIOLOGY]}>
                    <ListItem onClick={handleNewCaseClick} className='new-case-item'>
                      <ListItemIcon>
                        <AddIcon color='primary' />
                      </ListItemIcon>
                      <ListItemText primary="New Case" />
                    </ListItem>
                  </RestrictComponent>
                )}
              </List>
            ) : (
              <List>
                <ListItem className={dashboard.path === path && 'active-item'}>
                  <ListItemIcon>
                    <DashboardIcon />
                  </ListItemIcon>
                  <ListItemText primary={dashboard.label} />
                </ListItem>
              </List>
            )}
          </div>

          <Box className="sidebar-user-card">
            <Box className="sidebar-user-main" onClick={handleMenuOpen}>
              <Box className="sidebar-user-avatar">
                <Avatar
                  src={currentUser?.photoUrl}
                  alt={currentUser.firstName}
                  sx={{ width: 44, height: 44 }}
                />
                <span className="sidebar-user-online" />
              </Box>

              <Box sx={{ flex: 1, ml: 1 }}>
                <Typography className="sidebar-user-name">
                  {currentUser.firstName} {currentUser.lastName}
                </Typography>
                <Typography className="sidebar-user-role">
                  {displayUserRole(currentUser.role)}
                </Typography>
                {currentUser?.ghostMode && (
                  <Typography
                    sx={(theme) => ({
                      display: 'inline-flex',
                      mt: 0.75,
                      px: 1,
                      py: 0.25,
                      borderRadius: '999px',
                      fontSize: 12,
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? 'rgba(247, 144, 9, 0.16)' : '#FEF3F2',
                      color: theme.palette.mode === 'dark' ? '#fdb022' : '#b54708',
                    })}
                  >
                    Ghost Mode
                  </Typography>
                )}
              </Box>

              <IconButton size="small">
                <ArrowDown />
              </IconButton>
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              elevation={1}
            >
              <MenuItem onClick={() => {
                toggleUserProfileModal(true);
                handleMenuClose();
              }}>
                <EditIcon fontSize="small" style={{ marginRight: 8 }} color='secondary' />
                Edit Profile
              </MenuItem>
              <MenuItem onClick={() => {
                toggleColorMode();
                handleMenuClose();
              }}>
                {mode === 'dark' ? (
                  <LightModeIcon fontSize="small" style={{ marginRight: 8 }} color='secondary' />
                ) : (
                  <DarkModeIcon fontSize="small" style={{ marginRight: 8 }} color='secondary' />
                )}
                {mode === 'dark' ? 'Light mode' : 'Dark mode'}
              </MenuItem>
              <MenuItem onClick={signOut}>
                <LogoutIcon fontSize="small" style={{ marginRight: 8 }} color='secondary' />
                Logout
              </MenuItem>
            </Menu>
          </Box>

          {/*<div className="user-info">*/}
          {/*  <div className="user-name-container">*/}
          {/*    <div className="user-name">*/}
          {/*      {currentUser.role === 'Surgeon' && 'Dr. '} {currentUser.firstName}{' '}*/}
          {/*      {currentUser.lastName}*/}
          {/*    </div>*/}
          {/*    <div className="user-role">{displayUserRole(currentUser.role)}</div>*/}
          {/*    <div className="secondary m-t-md">{VERSION_NUMBER}</div>*/}
          {/*  </div>*/}
          {/*  <div className="sign-out-container" onClick={signOut}>*/}
          {/*    <img src={signOutIcon} alt="sign-out-icon" />*/}
          {/*  </div>*/}
          {/*</div>*/}
        </>
      )}
      {/*<RestrictComponent roles={[GLOBAL_ADMIN, COUNTRY_ADMIN, DISTRIBUTOR_ADMIN]}>*/}
      {/*  <AdminModal open={adminModalIsOpen} onClose={() => setAdminModalState(false)} />*/}
      {/*</RestrictComponent>*/}
      <CaseModal open={caseModalIsOpen} onClose={handleModalClose} />
      <DocumentsModal open={documentsModalIsOpen} onClose={() => setDocumentsModalState(false)} />
      <TrainingModeModal
        open={trainingModalIsOpen}
        onClose={() => setTrainingModalState(false)}
        onSubmit={handleSubmit}
        title="Training Mode"
        text="Use training mode to step through test cases and better understand features & functionality of the app."
        additionalText="In training mode, only test cases will be visible. You'll need to deactivate this function to view real cases."
        submitText="Activate"
      />
      <TrainingModeModal
        open={trainingConfirmModalOpen}
        onClose={() => setTrainingConfirmModalState(false)}
        onSubmit={() => {
          setTrainingConfirmModalState(false);
          setCaseModalState(true);
        }}
        title="Training Mode Active"
        text="You are about to create a case for training purposes only. Please cancel and deactivate training if you wish to create an actual case."
        submitText="Proceed"
      />
      <CaseExportModal
        open={isCaseExportModalOpen}
        onClose={() => {
          toggleCaseExportModal(false);
          discardActiveCase();
        }}
        caseId={caseId}
        onSkipClick={onSkipClick}
      />

      <UserProfileModal
        open={isUserProfileModalOpen}
        onClose={() => toggleUserProfileModal(false)}
        userFirstName={currentUser ? currentUser.firstName : ''}
        userLastName={currentUser ? currentUser.lastName : ''}
        userAddress={currentUser ? currentUser.address : ''}
        userPhone={currentUser ? currentUser.phone : ''}
        adminPassword={!!currentUser.adminPassword}
        currentUser={currentUser}
      />
    </div>
  );
};

Sidebar.propTypes = {
  currentUser: PropTypes.object,
  requiredCases: PropTypes.number,
  newActivities: PropTypes.number,
  discardActiveCase: PropTypes.func,
  closeSidebar: PropTypes.func,
};

const mapStateToProps = (state) => {
  const cases = state.cases.list.filter(filterRequired);
  const trainingMode = state.sidebar.trainingMode;
  const currentUser = state.user.currentUser;
  const companies = state.companies?.list || [];
  const company = state.companies?.list?.find((item) => item.id === currentUser?.company);
  const companyLogoLoading = !!currentUser?.company && !company && !companies.length;

  const requiredCases = cases.filter((item) => !!item.trainingMode === trainingMode && !item.archived && !item.postponed && item.status !== statuses.completed).length;

  return {
    currentUser,
    companySidebarLogoUrl: getCompanyLogoUrl(company?.sidebarLogo),
    companyLogoLoading,
    requiredCases,
    newActivities: state.activity.list.filter((item) => utils.checkTimeDiff(item.date) < 24).length,
    trainingMode,
    caseId: state.cases?.activeCase?.id,
    cases: state?.cases?.list || [],
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    discardActiveCase: () => dispatch(discardActiveCase()),
    closeSidebar: () => dispatch(closeSidebar()),
    turnOff: () => dispatch(turnTrainingModeOff()),
    turnOn: () => dispatch(turnTrainingModeOn()),
    setActiveCase: (caseItem) => dispatch(setActiveCase(caseItem))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withFirebase(withRouter(Sidebar)));
